import React, { useEffect, useState, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert } from "react-bootstrap";
import { FaPlus, FaTrash, FaArrowLeft, FaSearch, FaUsers, FaProjectDiagram } from "react-icons/fa";
import { useClientes } from '../../../services/hooks/useClientes';
import { useProyectos } from '../../../services/hooks/useProyectos';
import { useProyectoCliente } from '../../../services/hooks/useProyectoCliente';
import { Modal } from "antd";
import "../../../components/css/Clientes.css";

const MantenimientoProyectosClientes = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));
  
  // Hooks
  const { clientes, fetchClientes } = useClientes();
  const { 
    proyectosAsignados: clientesAsignados, 
    fetchClientesByProyecto, 
    asignarProyecto, 
    desasignarProyecto,
    loading: loadingClientes 
  } = useProyectoCliente();

  // Estados
  const [state, setState] = useState({
    searchTerm: "",
    clientesNoAsignados: [],
  });
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [asignacionToDelete, setAsignacionToDelete] = useState(null);
  const hasFetched = useRef(false);
  const lastProjectId = useRef(null);

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      // Verificar si el proyecto cambió o si es la primera carga
      const proyectoCambio = lastProjectId.current !== selectedProject?.id;
      
      if (proyectoCambio && selectedProject?.id) {
        try {
          // Resetear el flag cuando cambia el proyecto
          if (proyectoCambio) {
            hasFetched.current = false;
            lastProjectId.current = selectedProject.id;
          }
          
          if (!hasFetched.current) {
            await Promise.all([
              fetchClientes(),
              fetchClientesByProyecto(selectedProject.id)
            ]);
            hasFetched.current = true;
          }
        } catch (error) {
          console.error("Error al cargar datos iniciales:", error);
          toast.error("Error al cargar datos iniciales", {
            position: "bottom-right",
            autoClose: 2000
          });
        }
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id]); // Solo dependemos del ID del proyecto, no de las funciones

  // Calcular clientes no asignados
  useEffect(() => {
    const clientesNoAsignados = clientes.filter(cliente => 
      !clientesAsignados.some(asignado => asignado.id === cliente.id)
    );
    setState(prev => ({ ...prev, clientesNoAsignados }));
  }, [clientes, clientesAsignados]);

  // Filtrar clientes no asignados por término de búsqueda
  const filteredClientesNoAsignados = state.clientesNoAsignados.filter(cliente =>
    cliente.nombre.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
    cliente.numero_documento.toString().includes(state.searchTerm)
  );

  // Función para agregar cliente al proyecto
  const handleAgregarCliente = async () => {
    if (!selectedProject?.id || !clienteSeleccionado) {
      toast.error('Seleccione un cliente para asignar', {
        position: 'bottom-right',
        autoClose: 2000
      });
      return;
    }

    try {
      await asignarProyecto(parseInt(clienteSeleccionado), selectedProject.id);
      setClienteSeleccionado(''); // Limpiar selección
      // Recargar clientes asignados al proyecto para actualizar la grilla
      await fetchClientesByProyecto(selectedProject.id);
    } catch (error) {
      console.error('Error al asignar cliente:', error);
    }
  };

  // Función para confirmar eliminación
  const handleDelete = (clienteId) => {
    if (!selectedProject?.id) {
      toast.error('No hay proyecto seleccionado', {
        position: 'bottom-right',
        autoClose: 2000
      });
      return;
    }

    setAsignacionToDelete({
      clienteId: clienteId,
      proyectoId: selectedProject.id
    });
    setShowDeleteModal(true);
  };

  // Confirmar eliminación
  const confirmDelete = async () => {
    if (!asignacionToDelete) return;

    setShowDeleteModal(false);
    
    try {
      await desasignarProyecto(asignacionToDelete.clienteId, asignacionToDelete.proyectoId);
      // Recargar clientes asignados al proyecto
      await fetchClientesByProyecto(selectedProject.id);
    } catch (error) {
      console.error('Error al desasignar cliente:', error);
    } finally {
      setAsignacionToDelete(null);
    }
  };

  // Cancelar eliminación
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAsignacionToDelete(null);
  };

  const handleSearch = (event) => {
    setState(prev => ({ ...prev, searchTerm: event.target.value }));
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("selectedProject");
    navigate("/");
  };

  return (
    <>
      {/* Digital Harmony Styles para Mantenimiento Proyectos-Clientes */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 2rem;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            font-weight: 700;
          }
          
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          .dh-card-header-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-success:hover {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 8px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          .btn-dh-outline {
            border: 2px solid #667eea;
            color: #667eea;
            border-radius: 12px;
            padding: 8px 16px;
            font-weight: 600;
            background: white;
            transition: all 0.3s ease;
          }
          
          .btn-dh-outline:hover {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
          }
          
          .btn-dh-outline.selected {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border-color: #11998e;
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Enhanced Table */
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            text-transform: uppercase !important;
            padding: 1rem 0.75rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          .dh-enhanced-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          
          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }
          
          .dh-enhanced-table tbody tr.selected {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
            border-left: 4px solid #667eea;
          }
          
          /* Badge moderno */
          .dh-badge {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 10px;
            padding: 8px 12px;
            font-weight: 600;
            font-size: 0.85rem;
          }
          
          /* Área de formulario */
          .dh-form-area {
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            border: 2px solid #e5e7eb;
            border-radius: 15px;
            padding: 1.5rem;
            margin-bottom: 1.5rem;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaProjectDiagram className="me-3" />
                  Mantenimiento Proyectos - Clientes
                </h1>
                <p className="dh-main-subtitle">Gestión de asignaciones entre proyectos y clientes</p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Badge className="dh-badge">
                  Proyecto: {selectedProject?.nombre || "No seleccionado"}
                </Badge>
                <Button 
                  className="btn-dh-secondary d-flex align-items-center"
                  onClick={() => navigate("/home")}
                >
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
              </div>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              {/* Verificar que hay proyecto seleccionado */}
              {!selectedProject?.id ? (
                <Alert variant="warning" className="text-center">
                  <FaProjectDiagram className="me-2" />
                  No hay proyecto seleccionado. Por favor, seleccione un proyecto desde el menú principal.
                </Alert>
              ) : (
                <>
                  {/* Grilla de clientes asignados al proyecto */}
                  <Card className="dh-card">
                    <Card.Header className="dh-card-header dh-card-header-primary">
                      <h5 className="mb-0">
                        <FaUsers className="me-2" />
                        Clientes Asignados al Proyecto: {selectedProject.nombre}
                      </h5>
                    </Card.Header>
                    <Card.Body style={{ padding: '2rem' }}>
                      {clientesAsignados.length > 0 ? (
                        <div className="table-responsive">
                          <Table className="dh-enhanced-table">
                            <thead>
                              <tr>
                                <th>Nombre</th>
                                <th>Tipo Documento</th>
                                <th>Número Documento</th>
                                <th>Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientesAsignados.map(cliente => (
                                <tr key={cliente.id}>
                                  <td style={{ fontWeight: '600', color: '#374151' }}>{cliente.nombre}</td>
                                  <td>{cliente.tipo_documento?.codigo || 'N/A'}</td>
                                  <td>{cliente.numero_documento}</td>
                                  <td>
                                    <Button
                                      className="btn-dh-danger d-flex align-items-center"
                                      size="sm"
                                      onClick={() => handleDelete(cliente.id)}
                                      disabled={loadingClientes}
                                      title="Eliminar asignación"
                                    >
                                      <FaTrash className="me-2" size={12} />
                                      Eliminar
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <Alert variant="info" className="mb-0">
                          <FaUsers className="me-2" />
                          No hay clientes asignados a este proyecto. Use la sección de abajo para agregar clientes.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Área de asignación de clientes */}
                  <Card className="dh-card">
                    <Card.Header className="dh-card-header dh-card-header-success">
                      <h5 className="mb-0">
                        <FaPlus className="me-2" />
                        Asignación de Clientes
                      </h5>
                    </Card.Header>
                    <Card.Body style={{ padding: '2rem' }}>
                      {/* Área para agregar cliente */}
                      <div className="dh-form-area">
                        <h6 className="mb-3" style={{ color: '#667eea', fontWeight: '700' }}>Agregar Cliente</h6>
                        <Row className="align-items-end">
                          <Col md={8}>
                            <Form.Label style={{ fontWeight: '600', color: '#374151' }}>Seleccionar Cliente</Form.Label>
                            <Form.Select 
                              className="dh-form-control"
                              value={clienteSeleccionado}
                              onChange={(e) => setClienteSeleccionado(e.target.value)}
                            >
                              <option value="">-- Seleccione un cliente --</option>
                              {state.clientesNoAsignados.map(cliente => (
                                <option key={cliente.id} value={cliente.id}>
                                  {cliente.nombre} - {cliente.numero_documento}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={4}>
                            <Button 
                              className="btn-dh-success d-flex align-items-center"
                              onClick={handleAgregarCliente}
                              disabled={!clienteSeleccionado || loadingClientes}
                            >
                              <FaPlus className="me-2" size={14} />
                              {loadingClientes ? 'Agregando...' : 'Agregar'}
                            </Button>
                          </Col>
                        </Row>
                      </div>

                      {/* Lista de clientes disponibles para búsqueda */}
                      {state.clientesNoAsignados.length > 0 && (
                        <div className="dh-form-area">
                          <h6 className="mb-3" style={{ color: '#667eea', fontWeight: '700' }}>Clientes Disponibles</h6>
                          
                          {/* Barra de búsqueda */}
                          <div className="mb-3">
                            <div className="position-relative" style={{ maxWidth: '500px' }}>
                              <Form.Control
                                type="text"
                                className="dh-form-control"
                                placeholder="Buscar por nombre o número de documento..."
                                value={state.searchTerm}
                                onChange={handleSearch}
                                style={{ paddingLeft: '3rem' }}
                              />
                              <FaSearch 
                                className="position-absolute" 
                                style={{ 
                                  left: '1rem', 
                                  top: '50%', 
                                  transform: 'translateY(-50%)', 
                                  color: '#6b7280' 
                                }} 
                              />
                            </div>
                          </div>

                          {/* Lista filtrada de clientes no asignados */}
                          <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <Table className="dh-enhanced-table">
                              <thead>
                                <tr>
                                  <th>Nombre</th>
                                  <th>Tipo Doc.</th>
                                  <th>Número Documento</th>
                                  <th>Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredClientesNoAsignados.map((cliente) => (
                                  <tr key={cliente.id}>
                                    <td style={{ fontWeight: '600', color: '#374151' }}>{cliente.nombre}</td>
                                    <td>{cliente.tipo_documento?.codigo || 'N/A'}</td>
                                    <td>{cliente.numero_documento}</td>
                                    <td>
                                      <Button
                                        className="btn-dh-outline"
                                        size="sm"
                                        onClick={() => setClienteSeleccionado(cliente.id.toString())}
                                      >
                                        Seleccionar
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      )}

                      {state.clientesNoAsignados.length === 0 && (
                        <Alert variant="success" className="mb-0">
                          <FaUsers className="me-2" />
                          Todos los clientes están asignados a este proyecto.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal de confirmación de eliminación */}
      <Modal
        title="Confirmar Eliminación"
        open={showDeleteModal}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Eliminar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        <p>¿Está seguro de que desea eliminar esta asignación de proyecto?</p>
        <p>Esta acción no se puede deshacer.</p>
      </Modal>
    </>
  );
};

export default MantenimientoProyectosClientes;
